// ImportUsersTab — the Add-people drawer's CSV import per the design: an empty
// state (Choose file / Download template — click-to-select only, no drag-drop,
// per product decision), then once a file parses, a receipt card (counts +
// Replace file), a roles-for-imported-people pill row, and a preview DataTable
// of every row — flagged rows render muted with their reason and are excluded
// from the submit count.
//
// Validation happens the moment a file is picked (parsePeopleCsv), so `picked`
// only ever holds a file whose rows are already judged; file-level refusals
// toast and leave the dropzone as it was. The drawer owns the picked state,
// the role selections and the submit (its footer renders the counts and the
// Add-N-people button) — this component owns only the file plumbing and the
// preview rendering, mirroring how InviteByLinkTab leans on the drawer.
//
// Roles: every row carries its own list in `rowRoles` (keyed by file line,
// seeded from the shared selection when the file is picked). The shared chips
// are AUTHORITATIVE: any shared toggle replaces every row's list with the new
// shared set, so per-row adjustments hold only until the next shared change
// (user-specified). `effectiveRowRoles` resolves a row's list (exported so the
// drawer's submit maps rows with exactly the rule the preview displays).

import { useRef, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  Avatar,
  Button,
  Chip,
  DataTable,
  Icon,
  IconButton,
  MultiSectionCheckboxDropdown,
} from '@/components/common';
import type { DataTableColumn } from '@/components/common';
import type { RoleListItem } from '@/api/endpoints/rolesApi';
import { downloadTextFile } from '@/utils/downloadUrl';
import { parsePeopleCsv, type ImportPersonRow } from './parsePeopleCsv';
import { buildPeopleTemplateCsv, PEOPLE_TEMPLATE_FILE_NAME } from './peopleImportTemplate';
import initialsOf from '@/utils/initials';

/** A parsed file the drawer holds — rows carry their own per-row verdicts. */
export interface PickedPeopleCsv {
  fileName: string;
  rows: ImportPersonRow[];
}

/** The roles one row will import with — its own list, nothing else. There is
 *  deliberately NO fallback to the shared selection: the map is seeded per
 *  line at pick time, and a fallback would quietly re-couple unseeded rows to
 *  the shared chips, making a shared unselect read as replacing the row's
 *  whole list (the bug this signature removes). */
export const effectiveRowRoles = (
  line: number,
  rowRoles: Record<number, string[]>,
): string[] => rowRoles[line] ?? [];

interface ImportUsersTabProps {
  picked: PickedPeopleCsv | null;
  onPicked: (picked: PickedPeopleCsv) => void;
  /** Selectable roles (owner already filtered out by the drawer). */
  roles: RoleListItem[];
  roleIds: string[];
  onToggleRole: (id: string) => void;
  rowRoles: Record<number, string[]>;
  onToggleRowRole: (line: number, id: string) => void;
  /** Remove one row from the preview (and therefore from the batch). */
  onRemoveRow: (line: number) => void;
}

const dash = <span className='people-table__dash'>—</span>;

const ImportUsersTab = ({
  picked,
  onPicked,
  roles,
  roleIds,
  onToggleRole,
  rowRoles,
  onToggleRowRole,
  onRemoveRow,
}: ImportUsersTabProps) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const accept = async (file?: File) => {
    if (!file) return;
    const result = await parsePeopleCsv(file);
    if (result.status === 'ok') {
      onPicked({ fileName: file.name, rows: result.parsed.rows });
      return;
    }
    toast.error(t(result.errorKey, result.params));
  };

  const onPick = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Lets the same file be re-picked after a rejection; without it the input's
    // value never changes and onChange never fires again.
    e.target.value = '';
    void accept(file);
  };

  const downloadTemplate = () =>
    downloadTextFile(buildPeopleTemplateCsv(), PEOPLE_TEMPLATE_FILE_NAME);

  const readyCount = picked ? picked.rows.filter((row) => row.errors.length === 0).length : 0;
  const errorCount = picked ? picked.rows.length - readyCount : 0;
  // Every row — flagged ones included — must carry ≥1 role (mirrors the
  // drawer's disabled-gate condition exactly).
  const rolesMissing =
    !!picked && picked.rows.some((row) => effectiveRowRoles(row.line, rowRoles).length === 0);

  const columns: DataTableColumn<ImportPersonRow>[] = [
    {
      key: 'line',
      label: t('people_import_col_row'),
      // A flagged row carries its reason here (the muted row + receipt chip say
      // THAT something failed; this tooltip says what) since there is no Status
      // column — the trailing column is the row's Delete action.
      render: (row) => (
        <span className='people-import__line'>
          {row.line}
          {row.errors.length > 0 && (
            <span
              className='people-import__line-alert'
              title={row.errors.map((e) => t(e.errorKey, { column: e.column })).join(' · ')}
            >
              <Icon name='circle-alert' size={13} />
            </span>
          )}
        </span>
      ),
    },
    {
      key: 'person',
      label: t('people_col_person'),
      render: (row) => {
        const fullName = `${row.values.firstName} ${row.values.lastName}`.trim();
        return (
          <span className='people-table__person'>
            <Avatar initials={initialsOf(fullName) || '?'} size={34} />
            <span className='people-table__identity'>
              <span className='people-table__name'>{fullName || dash}</span>
              <span className='people-table__email'>
                {row.values.email || t('people_import_no_email')}
              </span>
            </span>
          </span>
        );
      },
    },
    {
      key: 'phone',
      label: t('people_col_phone'),
      render: (row) =>
        row.values.mobileNumber ? (
          <span className='people-table__phone' dir='ltr'>
            {`${row.values.countryCode} ${row.values.mobileNumber}`.trim()}
          </span>
        ) : (
          dash
        ),
    },
    {
      key: 'department',
      label: t('add_person_department'),
      render: (row) => (row.values.department ? <span>{row.values.department}</span> : dash),
    },
    {
      key: 'employment',
      label: t('add_person_emptype'),
      render: (row) =>
        row.values.employmentType ? <span>{row.values.employmentType}</span> : dash,
    },
    {
      key: 'idNumber',
      label: t('add_person_idnumber'),
      render: (row) =>
        row.values.personalIdNumber ? <span>{row.values.personalIdNumber}</span> : dash,
    },
    {
      key: 'idExpiry',
      label: t('add_person_idexpiry'),
      render: (row) =>
        row.values.personalIdExpiry ? <span>{row.values.personalIdExpiry}</span> : dash,
    },
    {
      key: 'birthDate',
      label: t('add_person_birthdate'),
      render: (row) => (row.values.birthDate ? <span>{row.values.birthDate}</span> : dash),
    },
    {
      key: 'nationality',
      label: t('people_col_nationality'),
      render: (row) => (row.values.nationality ? <span>{row.values.nationality}</span> : dash),
    },
    {
      key: 'roles',
      label: t('people_col_roles'),
      // Trigger-only cell (user decision): "Roles (N)" with the count badge;
      // the popover's checkboxes show and edit the row's effective set. The
      // common multi-select popover owns its own open state per row.
      render: (row) => (
        <MultiSectionCheckboxDropdown
          label={t('people_col_roles')}
          icon='shield-check'
          size='sm'
          plainTrigger
          showCount
          portal
          align='end'
          width={240}
          emptyText={t('text_no_results_found')}
          sections={[
            {
              key: 'roles',
              label: t('people_col_roles'),
              options: roles.map((role) => ({ id: role.id, label: role.name })),
            },
          ]}
          selected={{ roles: new Set(effectiveRowRoles(row.line, rowRoles)) }}
          onToggle={(_, id) => onToggleRowRole(row.line, id)}
        />
      ),
    },
    {
      key: 'remove',
      // Unlabeled header (user decision) — the trash glyph is the affordance.
      label: '',
      align: 'end',
      render: (row) => (
        <IconButton
          icon='trash-2'
          variant='ghost'
          size='sm'
          aria-label={t('button_delete')}
          onClick={() => onRemoveRow(row.line)}
        />
      ),
    },
  ];

  return (
    <div className='people-import'>
      <input ref={fileInputRef} type='file' accept='.csv,text/csv' hidden onChange={onPick} />

      {!picked ? (
        <div className='people-import__empty'>
          <span className='people-import__empty-icon' aria-hidden='true'>
            <Icon name='file-text' size={26} />
          </span>
          <span className='people-import__empty-title'>{t('people_import_empty_title')}</span>
          <span className='people-import__empty-hint'>{t('people_import_empty_hint')}</span>
          <span className='people-import__empty-actions'>
            <Button iconStart='upload' onClick={() => fileInputRef.current?.click()}>
              {t('people_import_choose')}
            </Button>
            <Button variant='secondary' iconStart='download' onClick={downloadTemplate}>
              {t('people_import_download')}
            </Button>
          </span>
        </div>
      ) : (
        <>
          {/* ---- parsed-file receipt ---- */}
          <div className='people-import__file'>
            <span className='people-import__file-icon' aria-hidden='true'>
              <Icon name='file-check' size={20} />
            </span>
            <span className='people-import__file-text'>
              <span className='people-import__file-name'>{picked.fileName}</span>
              <span className='people-import__file-meta'>
                {t('people_import_rows_parsed', { count: picked.rows.length })}
              </span>
            </span>
            <span className='people-import__file-chips'>
              {readyCount > 0 && (
                <Chip variant='success' iconStart='check'>
                  {t('people_import_ready_count', { count: readyCount })}
                </Chip>
              )}
              {errorCount > 0 && (
                <Chip variant='danger' iconStart='circle-alert'>
                  {t('people_import_error_count', { count: errorCount })}
                </Chip>
              )}
            </span>
            <Button
              variant='ghost'
              size='sm'
              iconStart='rotate-ccw'
              onClick={() => fileInputRef.current?.click()}
            >
              {t('people_import_replace')}
            </Button>
          </div>

          {/* ---- roles applied to every imported row ---- */}
          <section className='add-person__card'>
            <p className='add-person__card-head'>
              <Icon name='shield-check' size={16} />
              {t('people_import_roles_label')}
              {roleIds.length === 0 && (
                <span className='add-person__card-status'>{t('people_import_roles_none')}</span>
              )}
            </p>
            <div className='add-person__card-body'>
              <p className='people-import__roles-hint'>{t('people_import_roles_hint')}</p>
              <div className='add-person__pills'>
                {roles.map((role) => {
                  const selected = roleIds.includes(role.id);
                  return (
                    <button
                      key={role.id}
                      type='button'
                      className={`people-role-pill${selected ? ' people-role-pill--selected' : ''}`}
                      aria-pressed={selected}
                      onClick={() => onToggleRole(role.id)}
                    >
                      <Icon name={selected ? 'check' : 'shield-check'} size={15} />
                      {role.name}
                    </button>
                  );
                })}
              </div>
              {/* Proactive (not submit-gated): the footer button is DISABLED
                  while a row lacks a role, so this line is what explains why. */}
              {rolesMissing && (
                <p className='add-person__error'>
                  <Icon name='circle-alert' size={14} />
                  {t('people_import_roles_missing')}
                </p>
              )}
            </div>
          </section>

          {/* ---- preview of every parsed row (flagged rows muted) ---- */}
          <div className='people-import__table'>
            <DataTable
              columns={columns}
              rows={picked.rows}
              rowKey={(row) => String(row.line)}
              isRowError={(row) => row.errors.length > 0}
              density='compact'
              minWidth={1100}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default ImportUsersTab;
